import { useEffect, useRef, useState } from 'react';

import { Button, message, Modal, Tabs } from 'antd';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';
import * as Yup from 'yup';

import StationDoorAccessForm from './DoorAccess/StationDoorAccessForm';
import StationAddressForm from './StationAddressForm/StationAddressForm';
import StationConnectorsForm from './StationConnectorsForm';
import StationGeneralForm from './StationGeneralForm';
import StationPaymentForm from './StationPaymentForm';
import StationPhotosForm from './StationPhotosForm';
import { useTheme } from '../../../hooks/useTheme';
import { getChargePointGroups } from '../../../hooks/chargepointGroups';
import {
  createChargePoint,
  updateChargePoint,
  updateConnector,
} from '../../../hooks/chargepoints';
import { getConnectorTypes } from '../../../hooks/connectors';
import { getTenants } from '../../../hooks/tenants';
import { isIntelbrasTenant } from '../../../utils/isIntelbrasTenant/isIntelbrasTenant';
import { isProUser } from '../../../utils/isProUser/isProUser';
import { setChargePoint, setConnectorType } from '../../../redux/actions';
import { isMonetizationProUser } from '../../../utils/isMonetizationProUser/isMonetizationProUser';
import { getProName } from '../../../utils/getProName/getProName';
import { isTerpelTenant } from '../../../utils/isTerpelTenant/isTerpelTenant';
import { updateLicense } from '../../../hooks/license';
const StationConfigFormWrapper = styled.div`
  background-color: #fff;
  padding: 12px 24px 24px;
  border-radius: 3px;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.21);

  @media (min-width: 1024px) {
    width: 980px;
  }

  @media (min-width: 1366px) {
    width: 1240px;
  }

  .ant-tabs {
    width: 100%;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.primary};
  }

  .ant-tabs-tab {
    &:hover,
    &:active,
    &:focus {
      color: ${({ theme }) => theme.primary};
    }
  }

  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.primary};
  }

  .ant-tabs-top > .ant-tabs-nav {
    margin-bottom: 24px;
  }

  form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    label:first-child {
      margin-right: 10px;
    }

    .submit_button {
      margin: 24px 0 0 0;
    }

    .submit_button:not([disabled]) {
      background-color: ${({ theme }) => theme.primary};
      border-color: ${({ theme }) => theme.primary};
      display: flex;
      align-self: flex-end;
      border-radius: 3px;
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    &:hover,
    &:active,
    &:focus {
      border-color: ${({ theme }) => theme.primary};
    }
  }

  .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: ${({ theme }) => theme.primary};
  }

  .ant-switch-checked {
    background-color: ${({ theme }) => theme.primary};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StationConfigForm = ({
  initialValues,
  type,
  chargeBoxPk = null,
  usersPrivateStation,
  stationPhotos = [],
  setModalPushNotificationNewStationFast,
  licenseStatus,
  setLicenseStatus,
  licenseStatusLoading,
  setStationType,
}) => {
  const [connectorTypesList, setConnectorTypesList] = useState({});
  const [tenants, setTenants] = useState({});
  const [chargePointGroupList, setChargePointGroupList] = useState({});
  const [initialAddress, setInitialAddress] = useState({});
  const [initialLatitude, setInitialLatitude] = useState({});
  const [initialLongitude, setInitialLongitude] = useState({});
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [accessUsers, setAccessUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('item-1');
  const doorFormApi = useRef(null);
  const [files, setFiles] = useState([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const { theme } = useTheme();

  useEffect(() => {
    (async function () {
      setInitialAddress(initialValues.address);
      setInitialLatitude(initialValues.locationLatitude);
      setInitialLongitude(initialValues.locationLongitude);

      const connectorTypesData = await getConnectorTypes();
      setConnectorTypesList(connectorTypesData);
      const tenantsData = await getTenants();
      setTenants(tenantsData);
      const chargePointGroupsData = await getChargePointGroups();
      setChargePointGroupList(chargePointGroupsData);
    })();
  }, []);

  useEffect(() => {
    if (usersPrivateStation?.length > 0) {
      setOwnerUsers(
        usersPrivateStation
          .filter((user) => user.owner == true)
          .map((user) => user.user_pk),
      );
      setAccessUsers(
        usersPrivateStation
          .filter((user) => user.bind_status == 'ACCEPTED')
          .map((user) => user.user_pk),
      );
    }
  }, [usersPrivateStation]);

  useEffect(() => {
    if (stationPhotos && stationPhotos.length > 0) {
      setFiles(stationPhotos);
    }
  }, [stationPhotos]);

  const changeTab = (tab) => setActiveTab(tab);

  const authUser = JSON.parse(localStorage.getItem('auth-user'));

  const canAccessDoor = !!authUser?.permissions.some(
    (permission) =>
      permission.role_id === 'DASH_STATION_DOOR_ACCESS' && !!permission.status,
  );

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validate={(values) => {
        const errors = {};

        if (!values.licenseCode && isIntelbrasTenant() && isProUser()) {
          errors.licenseCode = T('The License Code field is mandatory.');
        }

        if (!values.chargeBoxId)
          errors.chargeBoxId = T('The ChargeBox ID field is mandatory.');
        if (values.chargeBoxId.indexOf(' ') >= 0)
          errors.chargeBoxId = T(
            'The ChargeBox ID field cannot contain blanks.',
          );
        if (!values.description)
          errors.description = T('The Description field is mandatory.');
        if (
          (values.hasSocPercentage === null) |
          (values.hasSocPercentage === undefined)
        )
          errors.hasSocPercentage = T(
            'The hasSocPercentage field is mandatory.',
          );
        if (values.typeOf === null)
          errors.typeOf = T('The Type field cannot be null.');
        if (values.showLocation || values.isHubjectCompatible) {
          if (!values.locationLatitude)
            errors.locationLatitude = T(
              'The Latitude field must be filled in.',
            );
          else if (
            values.locationLatitude < -90 ||
            values.locationLatitude > 90
          )
            errors.locationLatitude = T(
              'The Latitude field must be between -90 and 90.',
            );
          if (!values.locationLongitude)
            errors.locationLongitude = T(
              'The Longitude field must be filled in.',
            );
        }

        if (!values.isOpen_24Hours && !values.customizePerDay) {
          if (!values.openTime)
            errors.openTime = T('Opening time must be defined');
          if (!values.closeTime)
            errors.closeTime = T('Closing time must be defined');
        }
        if (!values.isOpen_24Hours && values.customizePerDay) {
          if (!values.schedules || values.schedules.length === 0) {
            errors.schedules = T('At least one day must have operating hours');
          }
        }
        errors.connectors = [];
        if (values?.connectors?.length) {
          values?.connectors?.length ??
            values.connectors.forEach((connector, index) => {
              if (
                connector.connectorTypeId === null ||
                !connector.connectorTypeId ||
                connector.connectorTypeId === 'Não especificado'
              )
                errors.connectors.push({
                  connectorType: T('The connector type field cannot be null.'),
                });
            });
        }
        if (!errors.connectors.length) delete errors.connectors;

        if (values.isTenantDeviceReader && !values.deviceReaderMac) {
          errors.deviceReaderMac = T(
            'The Facial Reader MAC Address field is mandatory.',
          );
        }

        return errors;
      }}
      validationSchema={() =>
        Yup.lazy((values) => {
          const isHubjectCompatible = values.isHubjectCompatible === true;
          return Yup.object().shape({
            address: Yup.object().shape({
              zipCode: isHubjectCompatible
                ? Yup.string().nullable().required(T('Required field'))
                : null,
              street:
                isHubjectCompatible || !values.street
                  ? Yup.string().nullable().required(T('Required field'))
                  : null,
              houseNumber:
                isHubjectCompatible || values.address.zipCode
                  ? Yup.string().nullable().required(T('Required field'))
                  : null,
              city: isHubjectCompatible
                ? Yup.string().nullable().required(T('Required field'))
                : null,
              state: isHubjectCompatible
                ? Yup.string().nullable().required(T('Required field'))
                : null,
            }),
          });
        })
      }
      onSubmit={async (values, { setSubmitting }) => {
        if (values?.connectors?.length) {
          const [connector] = values.connectors;

          if (connector.powerMaxCustom) {
            values.connectors = [
              {
                ...connector,
                powerMax: Number(connector.powerMaxCustom) * 1000,
              },
            ];
          }
        }

        if (values.address.country !== null) {
          values.address.empty = false;
        }

        if (parseFloat(values.taxIncome) > 0) {
          values.taxIncome = parseFloat(values.taxIncome) / 100;
        }

        if (values.typeOf == 'PRIVATE') {
          let concat_bind_users = ownerUsers.concat(accessUsers);
          concat_bind_users = [...new Set([...ownerUsers, ...accessUsers])];

          let bind_users;
          if (concat_bind_users.length) {
            bind_users = usersPrivateStation
              .filter((u) => concat_bind_users.includes(u.user_pk))
              .map((user) => {
                user.owner = false;
                user.bind_status = 'NOT_REQUESTED';
                if (ownerUsers.includes(user.user_pk)) {
                  user.owner = true;
                }
                if (accessUsers.includes(user.user_pk)) {
                  user.bind_status = 'ACCEPTED';
                }
                return {
                  user_pk: user.user_pk,
                  owner: user.owner,
                  bind_status: user.bind_status,
                  bind_exists: user.bind_exists,
                };
              });
          }
          values.bindUsers = bind_users;
        }

        if (!values.isTenantDeviceReader && values.deviceReaderMac) {
          values.deviceReaderMac = null;
        }

        if (values.licenseCode && licenseStatus !== 'Ativada') {
          try {
            await updateLicense({
              codigo: values.licenseCode,
              status: 'Ativada',
            });
          } catch (error) {
            if (error) {
              message.error(
                T('Erro ao atualizar licença') +
                `: ${error.response.data.error}`,
              );
              setSubmitting(false);
              return;
            }
          }
        }

        setTimeout(async () => {
          const payload = { ...values };

          // handle midnight time
          if (payload.closeTime === '00:00:00') {
            payload.closeTime = '23:59:59';
          }

          if (payload.isOpen_24Hours) {
            payload.schedule_type = '24H';
          } else if (payload.customizePerDay) {
            payload.schedule_type = 'CUSTOM';
          } else {
            payload.schedule_type = 'SINGLE';
          }
          payload.auto_stop_on_close = payload.autoStopOnClose || false;
          if (payload.schedules && payload.schedules.length > 0) {
            payload.schedules = payload.schedules.map((s) => ({
              day_of_week: s.dayOfWeek,
              start_time: s.startTime,
              end_time: s.endTime,
            }));
          } else {
            payload.schedules = [];
          }

          let data = null;

          if (type === 'create') {
            data = await createChargePoint(payload);
          } else if (type === 'update') {
            const changedConnectors = payload.connectors?.map(
              async (connector) => {
                await updateConnector(
                  {
                    connectorTypePk: connector.connectorTypeId,
                    powerMax: connector.powerMax,
                    moneyPerKilowattIncome: connector.moneyPerKilowattIncome,
                    moneyPerDurationIncome: connector.moneyPerDurationIncome,
                    unusualStatus: connector.unusualStatus || null,
                    idleConnectorFee: connector.idleConnectorFee || null,
                    idleFeeDefaultAmount: connector.idleFeeDefaultAmount,
                    // idleExempt* fields intentionally omitted:
                    // PUT /connector/{id} does not persist them (only the station
                    // PUT does, via ChargePointRestForm.connectors). Sending here
                    // is dead payload weight and confuses reviewers.
                  },
                  connector.connectorId,
                  payload.chargeBoxPk,
                );
              },
            );

            data = await updateChargePoint(payload, chargeBoxPk);
          }

          let doorFailures = [];
          if (type === 'update' && data?.type === 'success' && doorFormApi.current) {
            doorFailures = await doorFormApi.current.saveAll();
          }

          if (data !== null) {
            if (data.type === 'success') {
              if (type === 'create') {
                message.success(T('Station created successfully!'));

                setTimeout(() => {
                  history.push('/stations');
                }, 1000);
              } else {
                dispatch(setChargePoint(values, chargeBoxPk));
                const connectors = values.connectors?.map((connector) => {
                  dispatch(
                    setConnectorType(
                      connectorTypesList.list.find(
                        ({ id }) => id === parseInt(connector.connectorTypeId),
                      )?.name,
                      values.chargeBoxPk,
                      connector.connectorId,
                    ),
                  );
                });
                message.success(T('Station updated successfully!'));

                if (doorFailures.length) {
                  doorFailures.forEach(({ connectorId, error }) =>
                    message.error(
                      TF(
                        'The access door of connector {0} was not saved: {1}',
                        connectorId,
                        error,
                      ),
                    ),
                  );
                  setActiveTab('item-6');
                  setSubmitting(false);
                  return;
                }

                const hasChangedToFast = values.connectors.some((c, i) => {
                  const original = initialValues.connectors?.[i];
                  if (!original) return false;

                  const changed =
                    c.connectorTypeId !== original.connectorTypeId;
                  if (!changed) return false;

                  const typeInfo = connectorTypesList.list?.find(
                    (t) => String(t.id) === String(c.connectorTypeId),
                  );

                  return typeInfo?.speed === 'FAST';
                });

                const user = JSON.parse(localStorage.getItem('auth-user'));

                const shouldShowModalPush = !!user?.permissions.some(
                  (permission) =>
                    permission.role_id === 'DASH_SEND_PUSH_FAST_STATION' &&
                    !!permission.status,
                );

                if (hasChangedToFast && shouldShowModalPush) {
                  const { street, houseNumber, city, state } =
                    values?.address || {};

                  const addressString = street
                    ? `${street}${houseNumber ? ', ' + houseNumber : ''}${city ? ' - ' + city : ''
                    }${state ? '/ ' + state : ''}`
                    : '';

                  setModalPushNotificationNewStationFast({
                    visible: true,
                    subject: 'Nova estação de recarga DC',
                    message: `⚡ Nova estação de recarga DC rápida ${values.description || ''
                      } disponível: ${addressString}`,
                  });

                  return;
                }

                setTimeout(() => {
                  history.push('/stations');
                }, 1000);
              }
            } else {
              if (type === 'create') {
                if (data.status === 403) {
                  Modal.error({
                    title: 'Limite de estações atingido!',
                    content: (
                      <div style={{ fontWeight: 300 }}>
                        Caso deseje adicionar mais estações, será necessário
                        fazer upgrade para a versão{' '}
                        <strong style={{ fontWeight: 700 }}>
                          {getProName()}
                        </strong>{' '}
                        ou excluir alguma estação.
                      </div>
                    ),
                  });
                } else {
                  message.warning(
                    T(
                      'Unable to perform the operation. Make sure the fields are filled in correctly.',
                    ),
                  );
                }
              } else {
                message.warning(
                  T(
                    'Unable to perform the operation. Make sure the fields are filled in correctly.',
                  ),
                );
              }
              if (data.data && data.data.error) {
                await updateLicense({
                  codigo: values.licenseCode,
                  status: 'Criada',
                });

                const isDuplicateIdError =
                  data.data.error.includes('CHARGEBOX_ID_DUPLICATE') ||
                  data.data.error.includes('V010 - chargeBoxId_UNIQUE');

                const finalMessage = isDuplicateIdError
                  ? T(
                    'There is already a station registered with this identifier.',
                  ) + ` Error: ${data.data.error}`
                  : data.data.error;

                message.warning(finalMessage);
              }
            }
          } else {
            message.error(
              T(
                'Unable to perform the operation. Make sure the fields are filled in correctly.',
              ),
            );
          }

          setSubmitting(false);
        }, 400);
      }}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        isValid,
        setFieldError,
        validateForm,
      }) => {
        const backAddressFieldsToInitial = () => {
          values.address = initialAddress;
          values.locationLatitude = initialLatitude;
          values.locationLongitude = initialLongitude;
        };

        // Validate if cpGroup doesn't have Address to disabled address fields at Form
        var validateCpGroupAddress = chargePointGroupList.list?.find(
          (cp) => cp.chargeBoxGroupPk == values.chargeBoxGroupPk,
        )?.address?.street;

        return (
          <StationConfigFormWrapper>
            <form onSubmit={handleSubmit}>
              <Tabs activeKey={activeTab} onChange={changeTab}>
                <Tabs.TabPane tab={T('General')} key='item-1'>
                  <StationGeneralForm
                    chargeBoxPk={chargeBoxPk}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    type={type}
                    setStationType={setStationType}
                    backAddressFieldsToInitial={backAddressFieldsToInitial}
                    tenants={tenants}
                    chargePointGroupList={chargePointGroupList}
                    usersPrivateStation={usersPrivateStation}
                    setOwnerUsers={setOwnerUsers}
                    setAccessUsers={setAccessUsers}
                    setFieldValue={setFieldValue}
                    validateForm={validateForm}
                    licenseStatus={licenseStatus}
                    licenseStatusLoading={licenseStatusLoading}
                    setLicenseStatus={setLicenseStatus}
                  />
                </Tabs.TabPane>

                {values.connectors?.length ? (
                  <Tabs.TabPane tab={T('Technical settings')} key='item-2'>
                    {values.connectors?.length ? (
                      <StationConnectorsForm
                        values={values}
                        setFieldValue={setFieldValue}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        connectorTypesList={connectorTypesList}
                      />
                    ) : null}
                  </Tabs.TabPane>
                ) : null}

                <Tabs.TabPane tab={T('Address')} key='item-3'>
                  <StationAddressForm
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    validateCpGroupAddress={validateCpGroupAddress}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    isSubmitting={isSubmitting}
                    errors={errors}
                  />
                </Tabs.TabPane>

                {(isMonetizationProUser() || isTerpelTenant()) && (
                  <Tabs.TabPane tab={T('Payment')} key='item-4'>
                    <StationPaymentForm
                      values={values}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                  </Tabs.TabPane>
                )}

                <Tabs.TabPane tab={T('Photos')} key='item-5'>
                  <StationPhotosForm
                    values={values}
                    files={files}
                    setFiles={setFiles}
                  />
                </Tabs.TabPane>

                {canAccessDoor &&
                chargeBoxPk &&
                initialValues.connectors?.length ? (
                  <Tabs.TabPane tab={T('Access door')} key='item-6'>
                    <StationDoorAccessForm
                      chargeBoxPk={chargeBoxPk}
                      connectors={initialValues.connectors}
                      connectorTypesList={connectorTypesList}
                      formApiRef={doorFormApi}
                    />
                  </Tabs.TabPane>
                ) : null}
              </Tabs>

              <Footer>
                <div style={{ marginTop: 24 }}>
                  * {T('Required fields')}
                </div>

                <Button
                  loading={isSubmitting}
                  size='large'
                  className='submit_button'
                  type='primary'
                  disabled={
                    isSubmitting ||
                    (values.address.zipCode && !values.locationLongitude)
                  }
                  onClick={async () => {
                    if (
                      doorFormApi.current &&
                      !(await doorFormApi.current.validate())
                    ) {
                      setActiveTab('item-6');
                      message.error(T('Check the highlighted fields.'));
                      return;
                    }

                    if (!isValid) {
                      setActiveTab('item-1');
                    }

                    if (JSON.stringify(errors) !== '{}') {
                      message.error(
                        T(
                          'Unable to perform the operation. Make sure the fields are filled in correctly.',
                        ),
                      );
                    }
                    handleSubmit();
                  }}
                >
                  {T('Submit')}
                </Button>
              </Footer>
            </form>
          </StationConfigFormWrapper>
        );
      }}
    </Formik>
  );
};

export default StationConfigForm;
